import { parsearSSE } from "./sse";
import type { Evento, Fuente, Salud, SesionDetalle, SesionResumen, Sugerencia } from "./tipos";

/** Un error de la API con su código. Sin el código, la app no puede distinguir «hay que iniciar
 *  sesión» de «algo se rompió», y las dos cosas piden pantallas distintas. */
export class ErrorApi extends Error {
  constructor(public readonly status: number, public readonly ruta: string, public readonly cuerpo: string) {
    super(`${status} ${ruta}${cuerpo ? `: ${cuerpo.slice(0, 200)}` : ""}`);
    this.name = "ErrorApi";
  }

  get noAutorizado(): boolean {
    return this.status === 401;
  }
}

export function esNoAutorizado(e: unknown): boolean {
  return e instanceof ErrorApi && e.noAutorizado;
}

async function json<T>(ruta: string, opciones?: RequestInit): Promise<T> {
  const r = await fetch(ruta, { headers: { "content-type": "application/json" }, ...opciones });
  if (!r.ok) {
    throw new ErrorApi(r.status, ruta, await r.text().catch(() => ""));
  }
  return (await r.json()) as T;
}

export const api = {
  salud: () => json<Salud>("/healthz"),
  // El servidor pone una cookie HttpOnly, así que el token no queda al alcance de ningún script
  // de la página: el navegador lo manda solo. Por eso no se guarda en localStorage.
  login: (token: string) => json<{ usuario: string }>("/api/login", { method: "POST", body: JSON.stringify({ token }) }),
  /** Quién trae la cookie. Hace falta por el F5: `login` solo se llama una vez. */
  yo: () => json<{ usuario: string }>("/api/yo"),
  logout: () => json<{ fuera: boolean }>("/api/logout", { method: "POST" }),
  fuentes: () => json<{ fuentes: Fuente[]; corte_datos: { texto: string } }>("/api/fuentes"),
  sugerencias: () => json<{ sugerencias: Sugerencia[] }>("/api/sugerencias"),
  sesiones: () => json<SesionResumen[]>("/api/sesiones"),
  crearSesion: () => json<SesionResumen>("/api/sesiones", { method: "POST" }),
  sesion: (id: string) => json<SesionDetalle>(`/api/sesiones/${id}`),
  borrarSesion: (id: string) => json<{ borrada: string }>(`/api/sesiones/${id}`, { method: "DELETE" }),
  feedback: (cuerpo: { turno_id: string; voto: "arriba" | "abajo"; validado_por_especialista?: boolean; autor?: string; comentario?: string }) =>
    json<{ id: string; nota_creada: boolean; nota: string; aviso: string }>("/api/feedback", { method: "POST", body: JSON.stringify(cuerpo) }),
  urlExport: (id: string, formato: "md" | "html") => `/api/sesiones/${id}/export?formato=${formato}`,

  async *chat(mensaje: string, sesion_id: string | null, idioma: string | null, señal?: AbortSignal): AsyncGenerator<Evento> {
    const r = await fetch("/api/chat", {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify({ mensaje, sesion_id, idioma }),
      signal: señal,
    });
    if (!r.ok || !r.body) throw new Error(`${r.status} /api/chat`);
    yield* parsearSSE(r.body);
  },
};
